<template>
  <input
    v-if="forceInput"
    class="hidden"
    ref="fileInput"
    type="file"
    :multiple="multiple"
    @change="onFileInput"
    :accept="accept.join(',')"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { AlertDuration, AlertType, useAlertManager, useConfig } from "@pasta/box-v2";
import { FormOperation, type ParapheurConfig } from "../../types";
import xbytes from "xbytes";
import { useFileStore } from "../../stores/file.store";
import { useNotifyAsyncOperation } from "../../composable/useAsyncOperation";
import { useFileApi } from "../../http/file.api";
import { get } from "lodash";

interface Props {
  multiple?: boolean;
}

withDefaults(defineProps<Props>(), { multiple: false });

const emit = defineEmits<{
  (e: "uploaded", value: { fileId: number; file: File }[]): void;
}>();

const fileStore = useFileStore();
const fileApi = useFileApi();
const alertManager = useAlertManager();
const config = useConfig<ParapheurConfig>();
const notifyAsyncOperation = useNotifyAsyncOperation();

const accept = computed(() => {
  return [...fileStore.accepted.mimeTypes, ...fileStore.accepted.extensions.map(ext => `.${ext}`)];
});
const fileInput = ref<HTMLInputElement | null>(null);
const forceInput = ref<boolean>(true);
const isUploading = ref<boolean>(false);

async function onFileInput(e: Event) {
  const files: File[] = get(e, "target.files", []);
  await uploadFiles(files);
  // fix a bug on windows where it's not possible to upload the same file twice using the input
  // https://stackoverflow.com/questions/1703228/how-can-i-clear-an-html-file-input-with-javascript
  forceInput.value = false;
  await nextTick();
  forceInput.value = true;
}

async function uploadFiles(files: File[]) {
  if (isUploading.value) {
    alertManager.createAlert({
      message: "Un téléversement est déjà en cours, les fichiers sélectionnés n'ont pas été traités",
      duration: AlertDuration.MEDIUM,
      type: AlertType.WARNING,
    });
    return;
  }
  isUploading.value = true;
  try {
    await uploadEachFile(files);
  } finally {
    isUploading.value = false;
  }
}

async function uploadEachFile(files: File[]) {
  const sizeLimit = xbytes.parseSize(config.fileSizeLimit) || 5 * 1024 * 1024;

  const result: { fileId: number; file: File }[] = [];
  try {
    for (const file of files) {
      const accepted = fileStore.accepted;
      const extension = (file.name.split(".").at(-1) || "").toLowerCase();
      if (!accepted.extensions.includes(extension)) {
        alertManager.createAlert({
          message: `extension de fichier ".${extension}" non acceptée`,
          duration: AlertDuration.INFINITE,
          type: AlertType.WARNING,
        });
      } else if (file.size > sizeLimit) {
        alertManager.createAlert({
          message: `${file.name} est trop volumineux et n'a pas été traité (maximum: ${xbytes(sizeLimit).replaceAll(
            "B",
            "o"
          )})`,
          duration: AlertDuration.INFINITE,
          type: AlertType.WARNING,
        });
      } else {
        const form = new FormData();
        form.append("file", file);
        await notifyAsyncOperation(async () => {
          const { id } = await fileApi.uploadFile(form, "upload-document");
          result.push({ fileId: id, file });
        }, FormOperation.DOCUMENT);
      }
    }
  } catch {
    alertManager.createAlert({
      message: "Le téléversement a échoué",
      duration: AlertDuration.INFINITE,
      type: AlertType.ERROR,
    });
  }
  emit("uploaded", result);
}

function open() {
  fileInput.value?.click();
}

defineExpose({ open, uploadFiles, isUploading });
</script>
